import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  GraduationCap, Clock, DollarSign, BookOpen, CheckCircle2,
  Target, Users, Award, ArrowRight, CalendarDays, FileText,
  Briefcase, Star, ChevronLeft
} from 'lucide-react';

interface ProgramInfo {
  id: string;
  nameAr: string;
  nameEn: string;
  duration: string;
  fee: string;
  color: string;
  colorClass: string;
  borderColor: string;
  description: string;
  overview: string;
  objectives: string[];
  studySystem: {
    title: string;
    details: string[];
  };
  semesterStructure: {
    semester: string;
    description: string;
    courses: number;
  }[];
  requirements: string[];
  documents: string[];
  careers: string[];
  advantages: string[];
  specializations: string[];
  note?: string;
  targetStudents: string;
  minGrade: string;
}

const programs: Record<string, ProgramInfo> = {
  'diploma': {
    id: 'diploma',
    nameAr: 'الدبلوم المتوسط',
    nameEn: 'Intermediate Diploma',
    duration: 'سنتان (4 فصول دراسية)',
    fee: '600 دينار ليبي / فصل',
    color: 'from-blue-600 to-blue-800',
    colorClass: 'bg-blue-600',
    borderColor: 'border-blue-600',
    description: 'برنامج الدبلوم المتوسط هو برنامج أكاديمي مكثف مدته سنتان، يزوّد الطلاب بالمعرفة التقنية والمهارات العملية الأساسية في تخصصهم المختار.',
    overview: 'يمنح برنامج الدبلوم المتوسط الطلاب شهادة معتمدة من وزارة التعليم التقني والمهني، وتؤهلهم للدخول الفوري لسوق العمل أو الاستمرار في التعليم العالي للحصول على الدبلوم العالي.',
    objectives: [
      'إكساب الطالب المهارات التقنية الأساسية في مجال تخصصه',
      'تنمية القدرة على التطبيق العملي والتحليل',
      'تهيئة الطالب لسوق العمل المحلي والعالمي',
      'بناء أسس راسخة للتعليم المستمر والتطوير الذاتي',
      'تطوير مهارات التواصل والعمل الجماعي',
    ],
    studySystem: {
      title: 'نظام الساعات المعتمدة',
      details: [
        'الدراسة وفق نظام الساعات المعتمدة (Credit Hours)',
        '4 فصول دراسية (فصلان لكل سنة)',
        'الفصل الدراسي مدته 16 أسبوعاً',
        'إجمالي الساعات المعتمدة: 68 ساعة',
        'نظام التقييم: اختبارات، تقارير، مشاريع عملية',
        'الحضور الإلزامي: 75% على الأقل',
        'الحد الأدنى للنجاح: 50% في كل مادة',
      ],
    },
    semesterStructure: [
      { semester: 'الفصل الأول', description: 'مقدمة في التخصص والمواد الأساسية', courses: 6 },
      { semester: 'الفصل الثاني', description: 'المواد المتوسطة والمهارات التطبيقية', courses: 6 },
      { semester: 'الفصل الثالث', description: 'التخصصات المتقدمة والمشاريع', courses: 6 },
      { semester: 'الفصل الرابع', description: 'مشروع التخرج والتدريب الميداني', courses: 5 },
    ],
    requirements: [
      'شهادة الثانوية العامة أو ما يعادلها',
      'معدل لا يقل عن 50%',
      'اجتياز اختبار القبول',
      'إتمام إجراءات التسجيل في الموعد المحدد',
    ],
    documents: [
      'شهادة الثانوية العامة الأصلية وصورة منها',
      'صورة من بطاقة الهوية الوطنية',
      '4 صور شخصية حديثة',
      'شهادة الميلاد',
      'شهادة طبية تثبت اللياقة الصحية',
      'شهادة حسن سيرة وسلوك',
      'إيصال دفع رسوم التسجيل',
    ],
    careers: [
      'مساعد تقني في الشركات',
      'موظف دعم فني',
      'مساعد مبرمج',
      'موظف خدمة عملاء تقنية',
      'مساعد محاسب',
      'موظف إداري',
    ],
    advantages: [
      'شهادة معتمدة من وزارة التعليم التقني',
      'مدة دراسة قصيرة مع تأهيل مهني',
      'إمكانية الترقي للدبلوم العالي',
      'تدريب عملي في شركات محلية',
      'دعم طلابي متكامل',
    ],
    specializations: ['هندسة البرمجيات', 'هندسة الشبكات والاتصالات', 'إدارة الأعمال', 'المحاسبة المالية', 'التمويل والاستثمار', 'الإدارة والتنظيم القانوني'],
    targetStudents: 'خريجو الثانوية العامة الراغبون في الدخول السريع لسوق العمل',
    minGrade: '50%',
  },
  'advanced-diploma': {
    id: 'advanced-diploma',
    nameAr: 'الدبلوم العالي',
    nameEn: 'Advanced Diploma',
    duration: '3 سنوات (6 فصول دراسية)',
    fee: '650 دينار ليبي / فصل',
    color: 'from-indigo-600 to-indigo-800',
    colorClass: 'bg-indigo-600',
    borderColor: 'border-indigo-600',
    description: 'برنامج الدبلوم العالي هو برنامج أكاديمي متقدم مدته 3 سنوات، يوفر تعليماً تقنياً متعمقاً وشاملاً يجمع بين النظرية والتطبيق.',
    overview: 'يُعدّ الدبلوم العالي من أكثر البرامج طلباً في سوق العمل الليبي، إذ يمنح حاملها كفاءات تقنية متقدمة مع إمكانية الاستمرار للحصول على البكالوريوس عبر برنامج الاستكمال.',
    objectives: [
      'تأهيل الطلاب بكفاءات تقنية متقدمة',
      'تطوير مهارات التحليل والتصميم والإدارة',
      'تنمية قدرات البحث والابتكار',
      'بناء شبكة علاقات مهنية واسعة',
      'الإعداد للحصول على شهادة البكالوريوس',
    ],
    studySystem: {
      title: 'نظام الساعات المعتمدة المتقدم',
      details: [
        'الدراسة وفق نظام الساعات المعتمدة المتقدم',
        '6 فصول دراسية (فصلان لكل سنة)',
        'الفصل الدراسي مدته 16 أسبوعاً',
        'إجمالي الساعات المعتمدة: 102 ساعة',
        'نظام التقييم المتقدم: اختبارات + مشاريع + تقارير بحثية',
        'الحضور الإلزامي: 75% على الأقل',
        'الحد الأدنى للنجاح: 50% في كل مادة',
      ],
    },
    semesterStructure: [
      { semester: 'السنة الأولى', description: 'المواد الأساسية والتأسيسية', courses: 12 },
      { semester: 'السنة الثانية', description: 'المواد المتخصصة والتطبيقية', courses: 12 },
      { semester: 'السنة الثالثة', description: 'التخصصات المتقدمة ومشروع التخرج', courses: 11 },
    ],
    requirements: [
      'شهادة الثانوية العامة أو ما يعادلها',
      'معدل لا يقل عن 60%',
      'اجتياز اختبار القبول',
      'إتمام المقابلة الشخصية',
    ],
    documents: [
      'شهادة الثانوية العامة الأصلية وصورة منها',
      'صورة من بطاقة الهوية الوطنية',
      '4 صور شخصية حديثة',
      'شهادة الميلاد',
      'شهادة طبية تثبت اللياقة الصحية',
      'شهادة حسن سيرة وسلوك',
      'إيصال دفع رسوم التسجيل',
    ],
    careers: [
      'مهندس برمجيات / شبكات',
      'مدير مشاريع تقنية',
      'محلل أنظمة',
      'مدير حسابات',
      'مستشار إداري',
      'مطور تطبيقات',
    ],
    advantages: [
      'شهادة معتمدة دولياً',
      'تأهيل متكامل نظري وتطبيقي',
      'إمكانية استكمال البكالوريوس بسنة ونصف فقط',
      'فرص توظيف واسعة',
      'برامج تدريبية في شركات شريكة',
    ],
    specializations: ['هندسة البرمجيات', 'هندسة الشبكات والاتصالات', 'إدارة الأعمال', 'المحاسبة المالية', 'التمويل والاستثمار', 'الإدارة والتنظيم القانوني'],
    targetStudents: 'خريجو الثانوية الراغبون في تأهيل متقدم مع إمكانية الاستمرار للبكالوريوس',
    minGrade: '60%',
  },
  'bachelor': {
    id: 'bachelor',
    nameAr: 'البكالوريوس',
    nameEn: 'Bachelor\'s Degree',
    duration: '4 سنوات (8 فصول دراسية)',
    fee: '860 دينار ليبي / فصل',
    color: 'from-amber-600 to-amber-800',
    colorClass: 'bg-amber-600',
    borderColor: 'border-amber-600',
    description: 'برنامج البكالوريوس هو الدرجة الأكاديمية الأولى، ويُعدّ البرنامج الأكاديمي الأشمل والأعمق الذي تقدمه الكلية، مدته 4 سنوات.',
    overview: 'يمنح برنامج البكالوريوس حامله شهادة معتمدة من وزارة التعليم التقني والمهني في ليبيا، وتفتح له أبواب فرص عمل أوسع محلياً وعالمياً، مع إمكانية الالتحاق ببرامج الدراسات العليا.',
    objectives: [
      'تأهيل متخصصين على المستوى الأكاديمي العالي',
      'تطوير مهارات البحث العلمي والتفكير النقدي',
      'إعداد قادة المستقبل في مجالات التخصص',
      'بناء كوادر قادرة على الابتكار وريادة الأعمال',
      'التهيؤ لمواصلة الدراسات العليا',
    ],
    studySystem: {
      title: 'نظام البكالوريوس المتكامل',
      details: [
        'الدراسة وفق نظام الساعات المعتمدة الكامل',
        '8 فصول دراسية (فصلان لكل سنة)',
        'الفصل الدراسي مدته 16 أسبوعاً',
        'إجمالي الساعات المعتمدة: 136 ساعة',
        'نظام تقييم متكامل: اختبارات + مشاريع + بحوث + تدريب ميداني',
        'الحضور الإلزامي: 75% على الأقل',
        'مشروع تخرج شامل في السنة الرابعة',
      ],
    },
    semesterStructure: [
      { semester: 'السنة الأولى', description: 'المواد الأساسية والتأسيسية العامة', courses: 12 },
      { semester: 'السنة الثانية', description: 'المواد التخصصية الأساسية', courses: 12 },
      { semester: 'السنة الثالثة', description: 'التخصصات المتقدمة والتدريب', courses: 12 },
      { semester: 'السنة الرابعة', description: 'البحوث والمشاريع ومشروع التخرج', courses: 11 },
    ],
    requirements: [
      'شهادة الثانوية العامة أو ما يعادلها',
      'معدل لا يقل عن 65%',
      'اجتياز اختبار القبول',
      'اجتياز المقابلة الشخصية',
    ],
    documents: [
      'شهادة الثانوية العامة الأصلية وصورة منها',
      'صورة من بطاقة الهوية الوطنية',
      '4 صور شخصية حديثة',
      'شهادة الميلاد',
      'شهادة طبية',
      'شهادة حسن سيرة وسلوك',
      'إيصال دفع رسوم التسجيل',
    ],
    careers: [
      'مهندس متخصص',
      'مدير تنفيذي',
      'باحث علمي',
      'مستشار متخصص',
      'رائد أعمال',
      'أستاذ جامعي (بعد الدراسات العليا)',
    ],
    advantages: [
      'أعلى شهادة أكاديمية تمنحها الكلية',
      'اعتراف دولي واسع',
      'فرص عمل أوسع وراتب أعلى',
      'إمكانية الالتحاق بالدراسات العليا',
      'تأهيل بحثي وعملي متكامل',
    ],
    specializations: ['هندسة البرمجيات', 'هندسة الشبكات والاتصالات', 'إدارة الأعمال', 'المحاسبة المالية', 'التمويل والاستثمار', 'الإدارة والتنظيم القانوني'],
    targetStudents: 'خريجو الثانوية الراغبون في أعلى مستوى أكاديمي في تخصصاتهم',
    minGrade: '65%',
  },
  'bachelor-completion': {
    id: 'bachelor-completion',
    nameAr: 'استكمال البكالوريوس',
    nameEn: 'Bachelor\'s Completion Program',
    duration: 'سنة ونصف (4 فصول دراسية)',
    fee: '950 دينار ليبي / فصل',
    color: 'from-emerald-600 to-emerald-800',
    colorClass: 'bg-emerald-600',
    borderColor: 'border-emerald-600',
    description: 'برنامج استكمال البكالوريوس مخصص حصرياً لحاملي شهادة الدبلوم العالي، ويمنحهم فرصة الحصول على درجة البكالوريوس بمعدل فصل دراسي إلى أربعة فصول دراسية حسب إجراء معادلة الدبلوم العالي.',
    overview: 'يُعتبر هذا البرنامج فرصة ذهبية لخريجي الدبلوم العالي لرفع مؤهلاتهم الأكاديمية إلى مستوى البكالوريوس، مما يفتح أمامهم آفاقاً أوسع في سوق العمل ويمكّنهم من مواصلة الدراسات العليا.',
    objectives: [
      'استكمال متطلبات درجة البكالوريوس لحاملي الدبلوم العالي',
      'سد الفجوات المعرفية بين الدبلوم العالي والبكالوريوس',
      'تطوير مهارات البحث والتحليل المتقدم',
      'تأهيل الطلاب لفرص عمل أرقى وراتب أعلى',
      'التهيؤ لمرحلة الدراسات العليا',
    ],
    studySystem: {
      title: 'نظام الاستكمال المعجّل',
      details: [
        'برنامج مكثف مصمم خصيصاً لحاملي الدبلوم العالي',
        'من فصل دراسي واحد إلى أربعة فصول حسب المعادلة (سنة ونصف)',
        'يُعتمد رصيد الدبلوم العالي الكامل',
        'إجمالي ساعات الاستكمال: 50 ساعة معتمدة',
        'التركيز على المواد التكميلية والبحثية المتقدمة',
        'مشروع تخرج بحثي متكامل',
        'الحضور الإلزامي: 75% على الأقل',
      ],
    },
    semesterStructure: [
      { semester: 'الفصل الأول', description: 'المواد التكميلية المتقدمة', courses: 5 },
      { semester: 'الفصل الثاني', description: 'مواد التخصص العميق والبحث', courses: 5 },
      { semester: 'الفصل الثالث', description: 'مشروع التخرج والتدريب الميداني', courses: 4 },
      { semester: 'الفصل الرابع', description: 'التدريب الميداني والتقييم النهائي', courses: 3 },
    ],
    requirements: [
      'شهادة الدبلوم العالي من الكلية أو مؤسسة معترف بها',
      'معدل تراكمي لا يقل عن 65% في الدبلوم العالي',
      'موافقة مجلس القبول والتسجيل',
      'اجتياز اختبار التصنيف',
    ],
    documents: [
      'شهادة الدبلوم العالي الأصلية وصورة مصدّقة',
      'كشف الدرجات التفصيلي مصدّق',
      'صورة من بطاقة الهوية الوطنية',
      '4 صور شخصية حديثة',
      'شهادة الميلاد',
      'شهادة حسن سيرة وسلوك',
      'إيصال دفع رسوم التسجيل',
    ],
    careers: [
      'مناصب قيادية في الشركات',
      'مدير قسم تقني أو إداري',
      'مستشار متخصص رفيع',
      'رائد أعمال',
      'مدرس جامعي (بعد الدراسات العليا)',
      'باحث في مراكز الأبحاث',
    ],
    advantages: [
      'مدة دراسية قصيرة جداً (سنة ونصف)',
      'الاستفادة الكاملة من مؤهل الدبلوم العالي',
      'شهادة بكالوريوس معتمدة بأقل تكلفة',
      'جدول دراسي مرن يناسب المتزوجين والعاملين',
      'فتح باب الدراسات العليا',
    ],
    specializations: ['هندسة البرمجيات', 'هندسة الشبكات والاتصالات', 'إدارة الأعمال', 'المحاسبة المالية', 'التمويل والاستثمار', 'الإدارة والتنظيم القانوني'],
    note: 'هذا البرنامج مخصص حصرياً لحاملي شهادة الدبلوم العالي',
    targetStudents: 'حاملو شهادة الدبلوم العالي الراغبون في الحصول على البكالوريوس',
    minGrade: 'دبلوم عالي بمعدل 65%+',
  },
};

export default function ProgramDetail() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const program = programs[programId || ''];

  if (!program) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-bold">البرنامج غير موجود</h2>
            <Button onClick={() => navigate(ROUTE_PATHS.ADMISSIONS)}>
              العودة للقبول والتسجيل
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`bg-gradient-to-br ${program.color} text-white py-20`}
        >
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="max-w-4xl">
              <div className="flex items-center gap-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/80 hover:text-white hover:bg-white/20 gap-1"
                  onClick={() => navigate(ROUTE_PATHS.ADMISSIONS)}
                >
                  <ChevronLeft className="w-4 h-4" />
                  القبول والتسجيل
                </Button>
              </div>
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl md:text-6xl font-bold mb-3">{program.nameAr}</h1>
                  <p className="text-xl text-white/80 mb-4">{program.nameEn}</p>
                  <div className="flex flex-wrap gap-3">
                    <Badge className="bg-white/20 text-white border-white/30 text-base px-4 py-1">
                      <Clock className="w-4 h-4 ml-1" />
                      {program.duration}
                    </Badge>
                    <Badge className="bg-white/20 text-white border-white/30 text-base px-4 py-1">
                      <DollarSign className="w-4 h-4 ml-1" />
                      {program.fee}
                    </Badge>
                    {program.note && (
                      <Badge className="bg-yellow-400/30 text-yellow-100 border-yellow-400/40 text-sm px-4 py-1">
                        ⭐ {program.note}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Quick Stats */}
        <section className="py-8 bg-muted/50 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Clock, label: 'المدة', value: program.duration },
                { icon: DollarSign, label: 'الرسوم', value: program.fee },
                { icon: Award, label: 'الحد الأدنى', value: program.minGrade },
                { icon: Users, label: 'الفئة المستهدفة', value: 'مفتوح للجميع' },
              ].map((stat, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-background rounded-xl p-4 shadow-sm">
                  <div className={`w-10 h-10 rounded-lg ${program.colorClass} flex items-center justify-center flex-shrink-0`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="font-semibold text-sm">{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Overview */}
              <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <BookOpen className={`w-6 h-6 ${program.colorClass.replace('bg-', 'text-')}`} />
                  نظرة عامة
                </h2>
                <p className="text-muted-foreground leading-relaxed text-lg">{program.description}</p>
                <p className="text-muted-foreground leading-relaxed mt-3">{program.overview}</p>
              </motion.div>

              <Separator />

              {/* Objectives */}
              <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Target className={`w-6 h-6 ${program.colorClass.replace('bg-', 'text-')}`} />
                  أهداف البرنامج
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {program.objectives.map((obj, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-muted/40 rounded-xl p-4">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${program.colorClass.replace('bg-', 'text-')}`} />
                      <span className="text-sm leading-relaxed">{obj}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <Separator />

              {/* Study System */}
              <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <CalendarDays className={`w-6 h-6 ${program.colorClass.replace('bg-', 'text-')}`} />
                  نظام الدراسة
                </h2>
                <Card className={`border-r-4 ${program.borderColor}`}>
                  <CardHeader>
                    <CardTitle className="text-lg">{program.studySystem.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {program.studySystem.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${program.colorClass}`} />
                          <span className="text-foreground font-medium">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Semester Structure */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-4">هيكل البرنامج الدراسي</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {program.semesterStructure.map((sem, idx) => (
                      <motion.div
                        key={idx}
                        variants={staggerItem}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                      >
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-5">
                            <div className="flex items-center justify-between mb-3">
                              <div className={`w-10 h-10 rounded-full ${program.colorClass} text-white flex items-center justify-center font-bold`}>
                                {idx + 1}
                              </div>
                              <Badge variant="secondary">{sem.courses} مقررات</Badge>
                            </div>
                            <h4 className="font-bold mb-1">{sem.semester}</h4>
                            <p className="text-sm text-muted-foreground">{sem.description}</p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <Separator />

              {/* Careers */}
              <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Briefcase className={`w-6 h-6 ${program.colorClass.replace('bg-', 'text-')}`} />
                  الفرص الوظيفية
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {program.careers.map((career, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl p-4 text-center text-sm font-medium bg-gradient-to-br ${program.color} text-white`}
                    >
                      {career}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Fee Card */}
              <Card className={`border-2 ${program.borderColor} overflow-hidden`}>
                <div className={`bg-gradient-to-br ${program.color} p-6 text-white text-center`}>
                  <p className="text-sm opacity-80 mb-1">الرسوم الدراسية</p>
                  <p className="text-4xl font-bold">{program.fee.split(' / ')[0]}</p>
                  <p className="text-sm opacity-80">لكل فصل دراسي</p>
                </div>
                <CardContent className="p-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">مدة البرنامج:</span>
                    <span className="font-semibold">{program.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">الحد الأدنى:</span>
                    <span className="font-semibold">{program.minGrade}</span>
                  </div>
                  <Separator />
                  <Button
                    className={`w-full bg-gradient-to-r ${program.color} text-white hover:opacity-90`}
                    asChild
                  >
                    <Link to={ROUTE_PATHS.ADMISSIONS}>
                      سجّل الآن
                      <ArrowRight className="w-4 h-4 mr-2" />
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={ROUTE_PATHS.CONTACT}>
                      استفسر الآن
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Advantages */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Star className={`w-5 h-5 ${program.colorClass.replace('bg-', 'text-')}`} />
                    مميزات البرنامج
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {program.advantages.map((adv, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${program.colorClass.replace('bg-', 'text-')}`} />
                        <span className="text-muted-foreground">{adv}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Requirements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className={`w-5 h-5 ${program.colorClass.replace('bg-', 'text-')}`} />
                    شروط القبول
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {program.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${program.colorClass}`} />
                        <span className="text-muted-foreground">{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Documents */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className={`w-5 h-5 ${program.colorClass.replace('bg-', 'text-')}`} />
                    المستندات المطلوبة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {program.documents.map((doc, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-500" />
                        <span className="text-muted-foreground">{doc}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Specializations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BookOpen className={`w-5 h-5 ${program.colorClass.replace('bg-', 'text-')}`} />
                    التخصصات المتاحة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {program.specializations.map((spec, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
